import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Visibility, VisibilityOff, LocationOn } from '@mui/icons-material';
import { useAdminLoginMutation } from '@features/auth/api/authApi';
import { useDispatch } from 'react-redux';
import { setCredentials } from '@features/auth/store/authSlice';
import { decodeJWT } from '@shared/utils/jwt.utils';
import { authLogger } from '@shared/utils/logger';
import { env } from '@shared/config/env';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  
  const [adminLogin, { isLoading }] = useAdminLoginMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the page user was trying to access before being redirected to login
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  // Debug: Log when component mounts
  useEffect(() => {
    console.log('🔍 LoginPage mounted');
    console.log('📍 API Endpoint:', env.API_ENDPOINT);
    console.log('📍 Redirect after login:', from);
  }, [from]);

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setError('');

    console.log('🔐 Login form submitted', { email, password: '***' });

    // Basic validation
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    console.log('✅ Validation passed, calling API...');

    try {
      // Use RTK Query mutation
      console.log('📡 Making API call to:', '/auth/admin/login');
      const result = await adminLogin({ email, password }).unwrap();
      console.log('✅ Login successful:', result);
      
      // Decode JWT to extract user info
      const payload = decodeJWT(result.accessToken);
      const user = payload ? {
        id: payload.userId?.toString() || payload.sub, // Use userId if available, fallback to sub
        email: email,
        name: payload.name || email.split('@')[0] || 'User', // Use email prefix as name for now
        role: payload.role,
        mobile: payload.mobile || '',
        permissions: payload.permissions || {},
      } : undefined;
      
      authLogger.debug('User decoded:', user);
      
      // Dispatch to Redux store
      dispatch(setCredentials({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        accessExpiryMillis: result.accessExpiryMillis,
        user,
      }));
      
      authLogger.debug('Credentials dispatched to Redux');
      authLogger.debug('Navigating to:', from);
      
      // Navigate to dashboard
      setTimeout(() => {
        navigate(from, { replace: true });
        authLogger.debug('Navigation called');
      }, 100);
    } catch (err) {
      authLogger.error('Login failed', err);
      
      // Handle RTK Query error format
      const errorMessage = (err as { data?: { message?: string }; message?: string })?.data?.message || 
                          (err as { message?: string })?.message || 
                          'Login failed. Please check your credentials.';
      setError(errorMessage);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
      }}
    >
      {/* Left Side - Teal Gradient with Logo */}
      <Box
        sx={{
          flex: 1,
          background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 50%, #0f766e 100%)',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            top: '-200px',
            left: '-200px',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.05)',
            bottom: '-100px',
            right: '-100px',
          },
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            width: 180,
            height: 180,
            borderRadius: '40px',
            background: 'rgba(255, 255, 255, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            position: 'relative',
            zIndex: 1,
            mb: 4,
          }}
        >
          <Box
            sx={{
              width: 140,
              height: 140,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FDD835 0%, #FBC02D 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <LocationOn
              sx={{
                fontSize: 64,
                color: '#0891b2',
                mb: -1,
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#0891b2',
                letterSpacing: '3px',
              }}
            >
              KAPS
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="h4"
          sx={{
            color: 'white',
            fontWeight: 700,
            mb: 2,
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          KAPS Admin Portal
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255, 255, 255, 0.9)',
            textAlign: 'center',
            maxWidth: '400px',
            lineHeight: 1.8,
            position: 'relative',
            zIndex: 1,
          }}
        >
          Manage your transport operations with precision and control
        </Typography>
      </Box>

      {/* Right Side - Login Form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8fafc',
          padding: 3,
          overflow: 'auto',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: '480px',
            p: { xs: 4, sm: 6 },
            borderRadius: 3,
            backgroundColor: 'white',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
          }}
        >
          {/* Mobile Logo */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              justifyContent: 'center',
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #FDD835 0%, #FBC02D 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                }}
              >
                <LocationOn
                  sx={{
                    fontSize: 32,
                    color: '#0891b2',
                    mb: -0.5,
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: '#0891b2',
                    letterSpacing: '1.5px',
                    fontSize: '14px',
                  }}
                >
                  KAPS
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Header */}
          <Box mb={4}>
            <Typography
              variant="h4"
              fontWeight="700"
              sx={{
                color: '#0f172a',
                mb: 1.5,
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              Welcome Back
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                lineHeight: 1.6,
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              Sign in to access your admin dashboard
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                backgroundColor: '#fee2e2',
                color: '#991b1b',
                '& .MuiAlert-icon': {
                  color: '#dc2626',
                },
              }}
            >
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <Box display="flex" flexDirection="column" gap={3}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: '#334155',
                  }}
                >
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  placeholder="yourname@example.com"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  autoComplete="email"
                  autoFocus
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#f8fafc',
                      transition: 'all 0.2s',
                      '& fieldset': {
                        borderColor: '#e2e8f0',
                      },
                      '&:hover fieldset': {
                        borderColor: '#14b8a6',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#fff',
                        '& fieldset': {
                          borderColor: '#14b8a6',
                          borderWidth: '2px',
                        },
                      },
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: '#334155',
                  }}
                >
                  Password
                </Typography>
                <TextField
                  fullWidth
                  placeholder="••••••••"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  autoComplete="current-password"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#f8fafc',
                      transition: 'all 0.2s',
                      '& fieldset': {
                        borderColor: '#e2e8f0',
                      },
                      '&:hover fieldset': {
                        borderColor: '#14b8a6',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#fff',
                        '& fieldset': {
                          borderColor: '#14b8a6',
                          borderWidth: '2px',
                        },
                      },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePassword}
                          edge="end"
                          disabled={isLoading}
                          sx={{
                            color: '#64748b',
                            '&:hover': {
                              color: '#14b8a6',
                            },
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box display="flex" alignItems="center" justifyContent="space-between">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={isLoading}
                      sx={{
                        color: '#cbd5e1',
                        '&.Mui-checked': {
                          color: '#14b8a6',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="#64748b" fontWeight={500}>
                      Remember me
                    </Typography>
                  }
                />
              </Box>

              <Button
                fullWidth
                size="large"
                type="button"
                variant="contained"
                disabled={isLoading}
                onClick={handleSubmit}
                sx={{
                  py: 1.75,
                  mt: 1,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 600,
                  boxShadow: '0 4px 14px rgba(20, 184, 166, 0.4)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
                    boxShadow: '0 6px 20px rgba(20, 184, 166, 0.5)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&:disabled': {
                    background: '#cbd5e1',
                    boxShadow: 'none',
                  },
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>
          </form>

          {/* Footer */}
          <Box mt={4} textAlign="center">
            <Typography variant="caption" color="#94a3b8">
              © 2025 KAPS Trans Pvt. Ltd. All rights reserved.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};
